import { LoadingSpinner } from '@repo/atomic-components';
import { getDocumentViewerKind } from '@/utils/getDocumentViewerKind';
import { lazy, Suspense } from 'react';
import { usePreviewOutOfSync } from './usePreviewOutOfSync';
import { TextFileViewer } from './TextFileViewer';
import { ImageDocumentViewer } from './ImageDocumentViewer';
import { DOCUMENT_VIEWER_FILE_SIZE_TOO_LARGE_MESSAGE, isDocumentViewerFileSizeTooLarge } from './constants';

const VirtualizedPdfViewer = lazy(() =>
  import('@/ui/components/DocumentViewer/VirtualizedPdfViewer').then((module) => ({
    default: module.VirtualizedPdfViewer,
  })),
);

export type DocumentViewerProps = {
  fileUrl: string;
  documentId: string;
  documentName?: string;
  documentFormat?: string | null;
  documentSizeMb?: number | null;
  pdfControlsPosition?: 'center' | 'right';
  pdfSizingStrategy?: 'width' | 'height';
  highlightPageNumber?: number;
  highlightText?: string;
  highlightTrigger?: number;
};

export function DocumentViewer({
  fileUrl,
  documentId,
  documentName,
  documentFormat,
  documentSizeMb,
  pdfControlsPosition,
  pdfSizingStrategy,
  highlightPageNumber,
  highlightText,
  highlightTrigger,
}: DocumentViewerProps) {
  const kind = getDocumentViewerKind({ documentFormat, documentName, fileUrl });
  const previewOutOfSync = usePreviewOutOfSync(documentId, fileUrl);

  if (isDocumentViewerFileSizeTooLarge(documentSizeMb)) {
    return (
      <div className="border-theme-border-grey hide-scrollbar relative mx-0 h-full w-full overflow-auto border-l bg-transparent pt-14">
        <div className="text-theme-text-grey flex h-full w-full items-center justify-center p-6 text-center text-sm">
          {DOCUMENT_VIEWER_FILE_SIZE_TOO_LARGE_MESSAGE}
        </div>
      </div>
    );
  }

  if (previewOutOfSync) {
    return (
      <div className="border-theme-border-grey relative mx-0 h-full w-full overflow-hidden border-l bg-transparent">
        <div className="flex h-full w-full items-center justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (kind === 'pdf') {
    return (
      <Suspense
        fallback={
          <div className="border-theme-border-grey relative mx-0 h-full w-full overflow-hidden border-l bg-transparent">
            <div className="flex h-full w-full items-center justify-center">
              <LoadingSpinner />
            </div>
          </div>
        }
      >
        <VirtualizedPdfViewer
          key={`${documentId}-${fileUrl}`}
          documentId={documentId}
          fileUrl={fileUrl}
          documentName={documentName}
          controlsPosition={pdfControlsPosition}
          sizingStrategy={pdfSizingStrategy}
          highlightPageNumber={highlightPageNumber}
          highlightText={highlightText}
          highlightTrigger={highlightTrigger}
        />
      </Suspense>
    );
  }

  if (kind === 'text' || kind === 'markdown' || kind === 'rtf') {
    return (
      <div className="border-theme-border-grey hide-scrollbar relative mx-0 h-full w-full overflow-auto border-l bg-transparent pt-14">
        <TextFileViewer
          key={`${documentId}-${fileUrl}`}
          documentId={documentId}
          fileUrl={fileUrl}
          format={kind}
          highlightText={highlightText}
          highlightTrigger={highlightTrigger}
        />
      </div>
    );
  }

  if (kind === 'image') {
    return (
      <ImageDocumentViewer
        key={`${documentId}-${fileUrl}`}
        documentId={documentId}
        fileUrl={fileUrl}
        documentName={documentName}
        highlightText={highlightText}
        highlightTrigger={highlightTrigger}
      />
    );
  }

  return (
    <div className="border-theme-border-grey hide-scrollbar relative mx-0 h-full w-full overflow-auto border-l bg-transparent pt-14">
      <div className="text-theme-text-grey flex h-full w-full items-center justify-center p-6 text-sm">
        Preview unavailable for this file type.
      </div>
    </div>
  );
}
